/* eslint-disable jsx-a11y/no-autofocus */
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { getTown } from 'api/customerFlow'
import { Towns } from 'types/towns'
import { DeliveryPoint, PostalCodeFormInputs } from '../types'
import { Arrow } from '../assets'
import { PostalCode } from 'constants/regex'
import { useDispatch, useSelector } from 'react-redux'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { nextSimulationStep } from 'store/simulation/actions/switchSimulationStep'
import { Store } from 'store/types'
import NavigationButtons from '../components/navigation-buttons/NavigationButtons'
import { SimulationInputKeys } from 'store/simulation/types'
import { isValidPostalCode } from 'utils/helpers'
import { trackEvent } from 'utils/eventTracking'
import classNames from 'classnames'
import styles from './Steps.module.scss'
import { updateRegistrationInput } from 'store/registration/actions/updateRegistrationInputs'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/registration/types'
import { preventArrowUpAndDownEvent, preventInputScrollEvent } from 'utils/eventHandlers'

export const PostalCodeStep: FC = () => {
  // REDUX STORE
  const { inputs } = useSelector((state: Store) => state.simulation)
  const { inputs: registrationInputs } = useSelector((state: Store) => state.registration)
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation('simulation')

  // REACT HOOK FORM
  const {
    register,
    setError,
    clearErrors,
    getValues,
    formState: { errors }
  } = useForm<PostalCodeFormInputs>({
    mode: 'onChange',
    defaultValues: {
      postalCode: inputs.deliveryPoint.address.postalCode
    }
  })

  // STATE
  const [towns, setTowns] = useState<Towns>([])
  const [townCode, setTownCode] = useState<number>(-1)

  /**
   * Triggered on first render
   * Fetches towns when postalCode exists in store
   */
  useEffect(() => {
    if (inputs.deliveryPoint.address.postalCode) fetchTowns(inputs.deliveryPoint.address.postalCode)
  }, [])

  /**
   * Fetches the towns
   *
   * @param postalCode
   */
  const fetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      try {
        clearErrors()

        // Fetch the towns & the region
        const { towns: fetchedTowns, region } = await getTown(postalCode)

        // Set the fetched towns
        setTowns(fetchedTowns)
        setTownCode(fetchedTowns[0].townCode)

        // Update the region in the store
        dispatch(updateSimulationInput({ key: SimulationInputKeys.REGION, value: region }))
      } catch (e) {
        setError('postalCode', { type: 'api' })
      }
    } else {
      setError('postalCode', { type: 'validate' })
    }
  }

  /**
   * Handle the click on the selected town
   *
   * @param chosenTownCode
   */
  const handleTownClick = (chosenTownCode: number) => {
    setTownCode(chosenTownCode)
    const postalCode = getValues().postalCode
    const townName = towns.find((town) => town.townCode === chosenTownCode)?.townName || ''

    // Create the delivery point object
    const deliveryPoint: DeliveryPoint = {
      address: {
        postalCode: postalCode.toString(10),
        townName,
        townCode: chosenTownCode.toString(10)
      }
    }

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started', {
      postcode: postalCode.toString(10),
      step: 'postalcode'
    })

    // Update the fields in the store
    dispatch(updateSimulationInput({ key: SimulationInputKeys.DELIVERY_POINT, value: deliveryPoint }))

    // Update the fields in the registration store (postal code & town name are also used there)
    dispatch(
      updateRegistrationInput({
        group: InputGroupKeys.PERSONAL_DATA,
        key: PersonalDataFieldKeys.DELIVERY_ADDRESS,
        value: {
          ...registrationInputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.DELIVERY_ADDRESS],
          postalCode,
          townName,
          townCode: chosenTownCode.toString(10)
        }
      })
    )

    // Go to next step
    dispatch(nextSimulationStep())
  }

  return (
    <div>
      <div className="is-flex-column is-flex-centered">
        <h2 className={classNames(styles.title, 'margin-top-none', 'margin-bottom-l')}>{t('myPostalCodeTitle', 'Mijn postcode is...')}</h2>
      </div>
      <div className={styles['simulation-inputs']}>
        <div>
          <input
            type="number"
            min={0}
            max={9999}
            autoFocus
            className={classNames('border', { 'has-error': errors.postalCode })}
            inputMode="numeric"
            {...register('postalCode', { required: true, pattern: PostalCode, validate: (val) => isValidPostalCode(val) })}
            onChange={(e) => fetchTowns(parseInt(e.target.value, 10))}
            onWheel={(e) => preventInputScrollEvent(e)}
            onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
          />

          {!('postalCode' in errors) && towns.length > 0 && (
            <ul className={classNames(styles['list-towns'], 'margin-top-m')}>
              {towns.map((town, idx) => {
                return (
                  <li key={`${town.postalCode}-${idx}`} className={classNames({ [styles['is-selected']]: town.townCode === townCode })}>
                    <button onClick={() => handleTownClick(town.townCode)}>
                      <span>{town.townName}</span>
                      <Arrow />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {errors.postalCode && (
        <p className="is-red has-text-centered margin-top-m">
          {t('invalidPostalCode', 'Deze postcode is niet geldig of wordt (nog) niet beleverd door Bolt.')}
        </p>
      )}

      <NavigationButtons nextDisabled={'postalCode' in errors || towns.length === 0} onNextClick={() => handleTownClick(townCode)} />
    </div>
  )
}
